import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CalendarIcon from '../components/CalendarIcon';

// 프로젝트 표준 외곽선: 기본 회색 → hover/focus teal.
// 외부에서 sx로 borderColor를 따로 주면 자연스럽게 덮여진다.
const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': { borderColor: '#D9D9D9' },
    '&:hover fieldset': { borderColor: '#58B9C1', borderWidth: '2px' },
    '&.Mui-focused fieldset': { borderColor: '#58B9C1' },
  },
});

const KOREAN_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const DAY_ABBR_INDEX = { Su: 0, Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6 };

// AdapterDateFns는 Date 객체를 사용하므로 string/Date 모두 Date로 정규화한다.
const toDate = value => {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
};

// MUI x-date-pickers의 day 인자는 영문 약어("Mo", "Tu" ...) 또는 0-6 인덱스로 들어올 수 있다.
const formatKoreanWeekday = day => {
  if (typeof day === 'string' && day in DAY_ABBR_INDEX) {
    return KOREAN_DAYS[DAY_ABBR_INDEX[day]];
  }
  if (typeof day === 'number' && day >= 0 && day <= 6) {
    return KOREAN_DAYS[day];
  }
  return day;
};

const CommonDatePicker = ({ value, onChange, sx, disabled, minDate, placeholder, openOnClick }) => {
  const [open, setOpen] = React.useState(false);
  const dateValue = toDate(value);
  const parsedMinDate = toDate(minDate);

  // 1900년 이전이거나 잘못된 날짜가 입력된 채 포커스가 빠지면 빈값으로 정리.
  const handleBlur = () => {
    if (!value) return;
    const date = toDate(value);
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
      onChange?.(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <DatePicker
        sx={{ ...sx }}
        inputFormat="yyyy-MM-dd"
        value={dateValue}
        // 두 번째 인자는 키보드로 친 원문(달력 선택이면 undefined) — 쓰지 않는 화면은 무시하면 된다
        onChange={(date, keyboardInputValue) => onChange?.(date, keyboardInputValue)}
        minDate={parsedMinDate}
        disabled={!!disabled}
        showDaysOutsideCurrentMonth
        {...(openOnClick ? { open, onOpen: () => setOpen(true), onClose: () => setOpen(false) } : {})}
        components={{ OpenPickerIcon: CalendarIcon }}
        componentsProps={{
          actionBar: {
            actions: ['today'],
            todayButtonLabel: '오늘',
          },
        }}
        localeText={{
          cancelButtonLabel: '취소',
          clearButtonLabel: '초기화',
          okButtonLabel: '확인',
          todayButtonLabel: '오늘',
        }}
        dayOfWeekFormatter={formatKoreanWeekday}
        renderInput={params => (
          <StyledTextField
            sx={{ ...sx }}
            color="info"
            size="small"
            onBlur={handleBlur}
            {...params}
            {...(openOnClick ? { onClick: () => setOpen(true) } : {})}
            inputProps={{
              ...params.inputProps,
              placeholder: placeholder ?? params.inputProps?.placeholder,
              ...(openOnClick ? { readOnly: true, style: { cursor: 'pointer' } } : {}),
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default CommonDatePicker;
